import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import SVG from 'src/components/SvgIcon';
import useBindDispatch from 'src/hooks/useBindDispatch';
import {
  setReadingFeedbackVisibility as _setReadingFeedbackVisibility,
  setReadingFeedbackData as _setReadingFeedbackData,
  saveReadingFeedback as _saveReadingFeedback,
  fetchReadingTopics as _fetchReadingTopics,
  resetReadingFeedback as _resetReadingFeedback,
  showPopUpNotification as _showPopUpNotification,
} from 'actions/appActions';
import { sendPostMessageToParent } from 'src/sdk/service/sdk.service';
import { IAppState } from 'src/store/models';
import { DEMO_FOLLOW_UP_OPTIONS } from 'src/redux/sagas/readingFeedback/readingFeedbackSaga';
import { LocalStorage } from 'src/utils/storageHandler';
import { IS_MOBILE_INTEGRATION } from 'constants/constants';

import FollowUpSelect from './FollowUpSelect';
import styles from './ReadingFeedbackForm.module.scss';

const MEMORY_CLUE_MAX = 150;
const PEOPLE_DISCUSSED_MAX = 150;
const READING_COMMENTS_MAX = 1500;
const CLOSE_READING_FEEDBACK = 'closeReadingFeedback';

const normalizeText = (value: string) => value.trim().replace(/\s+/g, ' ');

const ReadingFeedbackForm: React.FC = () => {
  const {
    isFormVisible,
    isLoading,
    isWebViewMode,
    readingTopics,
    chatContext,
    formData,
  } = useSelector((state: IAppState) => state.app.readingFeedback);

  const setReadingFeedbackVisibility = useBindDispatch(_setReadingFeedbackVisibility);
  const setReadingFeedbackData = useBindDispatch(_setReadingFeedbackData);
  const saveReadingFeedback = useBindDispatch(_saveReadingFeedback);
  const fetchReadingTopics = useBindDispatch(_fetchReadingTopics);
  const resetReadingFeedback = useBindDispatch(_resetReadingFeedback);
  const showPopUpNotification = useBindDispatch(_showPopUpNotification);

  const isOpen = isLoading || isFormVisible;

  useEffect(() => {
    const isMobileIntegration = LocalStorage.getItem(IS_MOBILE_INTEGRATION);

    if (isOpen && isMobileIntegration) {
      const { scrollY } = window;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }

    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (isFormVisible && readingTopics.length === 0) {
      fetchReadingTopics();
    }
  }, [isFormVisible]);

  if (!isFormVisible) return null;

  const handleRating = (rating: 'thumbs_up' | 'thumbs_down') => {
    const newRating = formData.rating === rating ? null : rating;
    const update: any = { rating: newRating };

    if (newRating === 'thumbs_up') {
      if (formData.followUpTimeline === 'no_follow_up') {
        update.followUpTimeline = '';
      }
    } else if (newRating === 'thumbs_down') {
      update.followUpTimeline = 'no_follow_up';
    } else if (newRating === null && formData.rating === 'thumbs_down') {
      update.followUpTimeline = '';
    }

    setReadingFeedbackData(update);
  };

  const followUpOptions = formData.rating === 'thumbs_up'
    ? DEMO_FOLLOW_UP_OPTIONS.filter((opt) => opt.value !== 'no_follow_up')
    : DEMO_FOLLOW_UP_OPTIONS;

  const hasFormData = formData.rating !== null
    || !!formData.memoryClue?.trim()
    || formData.selectedTopics.length > 0
    || !!formData.followUpTimeline?.trim()
    || !!formData.peopleDiscussed?.trim()
    || !!formData.readingComments?.trim();

  const handleTopicToggle = (topic: string) => {
    const current = formData.selectedTopics;
    const updated = current.includes(topic)
      ? current.filter((t) => t !== topic)
      : [...current, topic];
    setReadingFeedbackData({ selectedTopics: updated });
  };

  const handleSave = () => {
    setReadingFeedbackData({
      memoryClue: normalizeText(formData.memoryClue),
      peopleDiscussed: normalizeText(formData.peopleDiscussed),
      readingComments: normalizeText(formData.readingComments),
    });
    saveReadingFeedback();
  };

  const handleCancel = () => {
    if (isWebViewMode) {
      sendPostMessageToParent({ command: CLOSE_READING_FEEDBACK });
    } else {
      setReadingFeedbackVisibility(false);
      resetReadingFeedback();
      showPopUpNotification(false);
    }
  };

  const renderThumbButton = (rating: 'thumbs_up' | 'thumbs_down', svgId: string) => (
    <button
      type="button"
      className={styles.thumbBtn}
      onClick={() => handleRating(rating)}
      aria-label={rating === 'thumbs_up' ? 'Thumbs up' : 'Thumbs down'}
    >
      <SVG id={formData.rating === rating ? `${svgId}Selected` : svgId} />
    </button>
  );

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerLabel}>Reading Feedback</span>
            <span className={styles.headerCustomerName}>
              {chatContext?.customerName || ' '}
            </span>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.headerTimeLabel}>Reading date and time:</span>
            <span className={styles.headerTime}>
              {chatContext?.readingTime || ''}
            </span>
          </div>
        </div>

        <div className={styles.body}>
          <div className={classNames(styles.sectionA, {
            [styles.bottomSpace]: !formData.selectedTopics.includes('Other'),
          })}
          >
            <div className={styles.row}>
              <span className={styles.rowLabel}>
                Did you have a psychic
                <br />
                {`connection with ${chatContext?.firstName}?`}
              </span>
              <div className={styles.thumbsGroup}>
                {renderThumbButton('thumbs_up', 'thumbsUp')}
                {renderThumbButton('thumbs_down', 'thumbsDown')}
              </div>
            </div>
            <div className={styles.followUP}>
              <FollowUpSelect
                label="Follow-up?"
                value={formData.followUpTimeline}
                options={followUpOptions}
                onChange={(val) => setReadingFeedbackData({ followUpTimeline: val })}
              />
            </div>
            <div>
              <div className={styles.topicsLabel}>Reading topic(s)</div>
              <div className={styles.chipsGrid}>
                {readingTopics.map((topic) => (
                  <button
                    key={topic}
                    type="button"
                    className={`${styles.chip} ${formData.selectedTopics.includes(topic) ? styles.chipSelected : ''}`}
                    onClick={() => handleTopicToggle(topic)}
                  >
                    {topic}
                  </button>
                ))}
              </div>
              {formData.selectedTopics.includes('Other') && (
                <p className={styles.otherHint}>Add Other specifics in Reading Details</p>
              )}
            </div>
          </div>

          <div className={styles.sectionB}>
            <div className={styles.sectionTitle}>Reading Details</div>

            <div className={styles.fieldGroup}>
              <label htmlFor="rf-memory-clue" className={styles.fieldLabel}>
                Memory Clue
                <div className={styles.inputWrapper}>
                  <input
                    id="rf-memory-clue"
                    className={styles.input}
                    type="text"
                    placeholder="Enter a memory clue"
                    maxLength={MEMORY_CLUE_MAX}
                    value={formData.memoryClue}
                    onChange={(e) => setReadingFeedbackData({
                      memoryClue: e.target.value.trimStart().slice(0, MEMORY_CLUE_MAX),
                    })}
                  />
                  {formData.memoryClue.length > 0 && (
                    <span className={styles.inputCharCount}>
                      {`${formData.memoryClue.length}/${MEMORY_CLUE_MAX}`}
                    </span>
                  )}
                </div>
              </label>
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="rf-people-discussed" className={styles.fieldLabel}>
                People discussed
                <div className={styles.inputWrapper}>
                  <input
                    id="rf-people-discussed"
                    className={styles.input}
                    type="text"
                    placeholder="Enter names of people discussed"
                    maxLength={PEOPLE_DISCUSSED_MAX}
                    value={formData.peopleDiscussed}
                    onChange={(e) => setReadingFeedbackData({
                      peopleDiscussed: e.target.value.trimStart().slice(0, PEOPLE_DISCUSSED_MAX),
                    })}
                  />
                  {formData.peopleDiscussed.length > 0 && (
                    <span className={styles.inputCharCount}>
                      {`${formData.peopleDiscussed.length}/${PEOPLE_DISCUSSED_MAX}`}
                    </span>
                  )}
                </div>
              </label>
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="rf-reading-comments" className={styles.fieldLabel}>
                Reading comments
                <div className={styles.textareaWrapper}>
                  <textarea
                    id="rf-reading-comments"
                    className={styles.textarea}
                    placeholder="Reading comments"
                    maxLength={READING_COMMENTS_MAX}
                    value={formData.readingComments}
                    onChange={(e) => setReadingFeedbackData({
                      readingComments: e.target.value.trimStart().slice(0, READING_COMMENTS_MAX),
                    })}
                  />
                  {formData.readingComments.length > 0 && (
                    <span className={styles.charCount}>
                      {`${formData.readingComments.length}/${READING_COMMENTS_MAX}`}
                    </span>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={!hasFormData}>
            Save
          </button>
          <button type="button" className={styles.cancelBtn} onClick={handleCancel}>
            cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReadingFeedbackForm;
